import { FC, useState } from 'react';
import { tripRowKeys } from 'utils/shared/tripBuilder';
import { TripSlotRequirement } from 'ts/shared/interfaces/CreateProposalParams';
import { useTripBuilderPreview } from 'contexts/TripBuilderPreviewContext';
import {
  TripPreviewFlightsRow,
  TripPreviewHotelOptionRow,
  TripPreviewHotelsRow,
  TripPreviewOptionRow,
  TripPreviewTransfersRow,
} from './TripBuilderPreviewCards';
import { TripBuilderSlotDraftPlan } from 'utils/shared/tripResultBuilderIdb';
import { TripPreviewSlotView } from 'utils/shared/tripResultBuilderPreview';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import CityNameModal from '../CityNameModal';

const TripBuilderPreviewSlots: FC<{ plans: TripBuilderSlotDraftPlan[] }> = ({ plans }) => {
  const { t } = useTranslation();
  const {
    canSave,
    requirementByRow,
    hotelImageBySlot,
    setRowRequirement,
    setHotelImageForSlot,
    selectBeforeFlight,
    selectAfterFlight,
    selectMiddleFlight,
    onSaveCityName,
  } = useTripBuilderPreview();
  const [editingCitySlot, setEditingCitySlot] = useState<{
    slotKey: string;
    city: string;
    checkIn: string;
    checkOut: string;
  } | null>(null);

  const getRowRequirement = (rowKey: string, defaultValue: TripSlotRequirement) =>
    requirementByRow[rowKey] ?? defaultValue;

  const renderMiddleSection = (
    slot: TripPreviewSlotView,
    middle: TripBuilderSlotDraftPlan['nodes'][number]['middleSections'][number],
    key: string,
  ) => (
    <TripPreviewOptionRow
      key={key}
      section={middle.section}
      requirementValue={getRowRequirement(
        tripRowKeys.middle(middle.section.transitionKey || ''),
        'optional',
      )}
      onRequirementChange={
        canSave
          ? (value) => setRowRequirement(tripRowKeys.middle(middle.section.transitionKey || ''), value)
          : undefined
      }
    >
      <TripPreviewFlightsRow
        flights={middle.section.flights}
        draftFlights={middle.draftFlights}
        title={middle.section.sectionTitle}
        onSelectFlight={(flightKey, isRoundTrip) =>
          selectMiddleFlight(slot, middle.section.transitionKey || '', flightKey, isRoundTrip)
        }
      />
    </TripPreviewOptionRow>
  );

  return (
    <>
      <div className="pr-tb-slots flex-column">
        {plans.map(
          ({
            slot,
            beforeSection,
            afterSection,
            draftBeforeFlights,
            draftAfterFlights,
            nodes,
          }) => (
            <div className="pr-tb-slot flex-column" key={slot.rootSlotKey}>
              {beforeSection && (
                <TripPreviewOptionRow
                  section={beforeSection}
                  requirementValue={getRowRequirement(tripRowKeys.before(slot.rootSlotKey), 'mandatory')}
                  onRequirementChange={
                    canSave
                      ? (value) => setRowRequirement(tripRowKeys.before(slot.rootSlotKey), value)
                      : undefined
                  }
                  dateBadge={slot.startDateBadge}
                >
                  <TripPreviewFlightsRow
                    flights={slot.beforeSection?.flights || []}
                    draftFlights={draftBeforeFlights}
                    onSelectFlight={(flightKey, isRoundTrip) =>
                      selectBeforeFlight(slot, flightKey, isRoundTrip)
                    }
                  />
                </TripPreviewOptionRow>
              )}

              {nodes.map(
                (
                  {
                    node,
                    draftHotels,
                    draftTransfers,
                    transferSection,
                    middleSections,
                    draftOnlyMiddleSections,
                  },
                  nodeIndex,
                ) => (
                  <div className="pr-tb-block flex-column" key={`${slot.rootSlotKey}-node-${nodeIndex}`}>
                    <div className="pr-tb-meta flex-between">
                      <span className="pr-tb-city text-md text-bold">{node.city}{canSave && (
                        <Tooltip content={t('proposal.edit')}>
                          <span
                            className="display-inline-flex cur-poi color-contrast ml-10"
                            onClick={() =>
                              setEditingCitySlot({
                                slotKey: node.slotKey,
                                city: node.city,
                                checkIn: node.checkIn,
                                checkOut: node.checkOut,
                              })
                            }
                          >
                            <FontAwesomeIcon icon={faPencil} style={{ width: 14, height: 14 }} size="sm" />
                          </span>
                        </Tooltip>
                      )}</span>

                      <span className="pr-tb-date text-xsm">({node.dateLabel})</span>
                    </div>
                    {!node.hotelSection && !draftHotels.length && <div className="pr-tb-plus flex-center">+</div>}
                    {(node.hotelSection || draftHotels.length > 0) && (
                      <TripPreviewHotelOptionRow
                        section={
                          node.hotelSection || {
                            requirementLabel: 'Mandatory',
                            flights: [],
                            hotels: [],
                            transfers: [],
                          }
                        }
                        requirementValue={getRowRequirement(tripRowKeys.hotel(node.slotKey), 'mandatory')}
                        onRequirementChange={
                          canSave
                            ? (value) => setRowRequirement(tripRowKeys.hotel(node.slotKey), value)
                            : undefined
                        }
                        imageUrl={hotelImageBySlot[node.slotKey]}
                        city={node.city}
                        onImageUrlChange={
                          canSave ? (url) => setHotelImageForSlot(node.slotKey, url) : undefined
                        }
                      >
                        <TripPreviewHotelsRow
                          hotels={node.hotelSection?.hotels || []}
                          draftHotels={draftHotels}
                          slotKey={node.slotKey}
                        />
                      </TripPreviewHotelOptionRow>
                    )}
                    {transferSection && (
                      <TripPreviewOptionRow
                        section={transferSection}
                        requirementValue={getRowRequirement(tripRowKeys.transfer(node.slotKey), 'mandatory')}
                        onRequirementChange={
                          canSave
                            ? (value) => setRowRequirement(tripRowKeys.transfer(node.slotKey), value)
                            : undefined
                        }
                      >
                        <TripPreviewTransfersRow
                          transfers={node.transferSection?.transfers || []}
                          draftTransfers={draftTransfers}
                          slotKey={node.slotKey}
                        />
                      </TripPreviewOptionRow>
                    )}
                    {middleSections.map((middle) =>
                      renderMiddleSection(slot, middle, middle.section.transitionKey || ''),
                    )}
                    {draftOnlyMiddleSections.map((middle) =>
                      renderMiddleSection(slot, middle, `draft-${middle.section.transitionKey}`),
                    )}
                  </div>
                ),
              )}

              {afterSection && (
                <TripPreviewOptionRow
                  section={afterSection}
                  requirementValue={getRowRequirement(tripRowKeys.after(slot.rootSlotKey), 'mandatory')}
                  onRequirementChange={
                    canSave
                      ? (value) => setRowRequirement(tripRowKeys.after(slot.rootSlotKey), value)
                      : undefined
                  }
                  dateBadge={slot.endDateBadge}
                >
                  <TripPreviewFlightsRow
                    flights={slot.afterSection?.flights || []}
                    draftFlights={draftAfterFlights}
                    onSelectFlight={(flightKey, isRoundTrip) =>
                      selectAfterFlight(slot, flightKey, isRoundTrip)
                    }
                  />
                </TripPreviewOptionRow>
              )}
            </div>
          ),
        )}
      </div>
      {editingCitySlot && (
        <CityNameModal
          close={() => setEditingCitySlot(null)}
          onSave={(cityName) => {
            onSaveCityName(editingCitySlot, cityName);
            setEditingCitySlot(null);
          }}
          cityName={editingCitySlot.city}
        />
      )}
    </>
  );
};

export default TripBuilderPreviewSlots;
